import {
  ToggleOption_CheckoutConfigOptionGroupFragment,
  ToggleOption_CheckoutConfigPotentialOptionFragment,
} from "src/fragments/circularFragments.generated";
import { MissingInsideMapsRenderingWarning } from "src/pages/configurator/components";
import { CutoffDaysRemaining } from "src/pages/configurator/components/options";
import { useConfiguratorContext } from "src/pages/configurator/Configurator.context";
import { InteriorStep_CheckoutOptionGroupFragment } from "src/pages/configurator/stepTemplates/Interior.step.generated";
import {
  Carousel,
  Checkbox,
  DebugText,
  LoadingBone,
  LoadingCarousel,
  OptionThumbnail,
  OverflowTooltip,
} from "~components";
import { Css } from "~generated/css";
import { formatDeltaPriceDisplay, useTestIds } from "~utils";
import { filterUniqueProducts } from "./packageOption.utils";

type PackageOptionCardProps = {
  onSelect: (
    group: ToggleOption_CheckoutConfigOptionGroupFragment,
    option: ToggleOption_CheckoutConfigPotentialOptionFragment,
  ) => void;
  disabled?: boolean;
  // Internally sets selected state based on selectedOptionIds instead of checking option.isSelected
  // for instant feedback
  selectedOptionIds: string[];
  ccfgOptionGroup: InteriorStep_CheckoutOptionGroupFragment;
  onCompare: (maybeChildCCOG?: InteriorStep_CheckoutOptionGroupFragment) => void;
  autoSaving: boolean;
  // Returns option selector (package option list) only
  asChild?: boolean;
};

/**
 * Renders list of package of options.
 */
export function PackageOptionCard(props: PackageOptionCardProps) {
  const {
    selectedOptionIds,
    onSelect,
    disabled = false,
    ccfgOptionGroup,
    onCompare,
    autoSaving,
    asChild = false,
  } = props;

  const { group, options, cutoffDate } = ccfgOptionGroup;
  const { marketingDescription, name } = group;

  const tid = useTestIds(props, "package-option-card");

  const OptionSelector = (
    <>
      {options.map((option) => (
        <PackageOption
          key={option.option.id}
          option={option}
          isSelected={selectedOptionIds.includes(option.option.id)}
          onSelect={() => onSelect(ccfgOptionGroup, option)}
          autoSaving={autoSaving}
          disabled={disabled}
          description={marketingDescription}
          // When rendered as a child under ProductAddOnCard, the parent card owns the warning
          asChild={asChild}
        />
      ))}
    </>
  );

  if (asChild) {
    return OptionSelector;
  }

  return (
    <div
      css={{
        ...Css.py3.w100.gap2.br4.df.fdc.add("transition", "0.2s").$,
        ...Css.if(disabled).cursorNotAllowed.bgGray200.gray400.$,
      }}
      {...tid}
    >
      {/* Group Header */}
      <div css={Css.df.jcsb.aic.$}>
        <div css={Css.t16.$}>
          <DebugText>{group.id}</DebugText>
          {name}
        </div>
        <div css={Css.df.gap1.aic.$}>
          <button type="button" onClick={() => onCompare()} css={Css.t10.gray.cursorPointer.$}>
            Compare
          </button>
          {cutoffDate && <CutoffDaysRemaining cutoffDate={cutoffDate} />}
        </div>
      </div>
      {/*  Group Description  */}
      <div css={Css.t12.gray.$}>{marketingDescription}</div>
      {/*  Group Options  */}
      <div css={Css.df.fdc.gap2.$}>{OptionSelector}</div>
    </div>
  );
}

type PackageOptionProps = {
  option: InteriorStep_CheckoutOptionGroupFragment["options"][number];
  isSelected: boolean;
  onSelect: () => void;
  autoSaving: boolean;
  disabled: boolean;
  description?: string | null;
  asChild?: boolean;
};

export function PackageOption(props: PackageOptionProps) {
  const { option, isSelected, onSelect, autoSaving, disabled, description, asChild = false } = props;
  const { option: optionFragment, totalPriceInCents } = option;
  const { name } = optionFragment;
  const { getProductDetails, productDetailsLoading } = useConfiguratorContext();

  const productDetails = getProductDetails(option.id);
  const displayProducts = productDetails?.displayProducts ?? [];
  const showLoadingCarousel = productDetailsLoading && displayProducts.isEmpty;

  // The sorting logic is moved to the backend, we don't have Range deduping will work
  const products = filterUniqueProducts(displayProducts);

  return (
    <div css={Css.df.fdc.gap2.if(disabled).cursorNotAllowed.$}>
      {/* selector header */}
      <div css={Css.df.jcsb.aic.t14.gap1.$}>
        <DebugText>{option.id}</DebugText>
        <div>{name}</div>
        <div css={Css.df.gap1.aic.$}>
          {autoSaving ? <LoadingBone width="58px" /> : <div>{formatDeltaPriceDisplay(totalPriceInCents)}</div>}
          <Checkbox onClick={onSelect} selected={isSelected} disabled={disabled} />
        </div>
      </div>
      {description && <OverflowTooltip id={option.id} text={description} xss={Css.t12.gray.lineClamp2.$} />}
      {/* product carousel */}
      {showLoadingCarousel ? (
        <LoadingCarousel count={3} size={72} />
      ) : (
        <Carousel
          items={products.map((product) => {
            const { featuredImage, displayName, id: productId } = product;
            return (
              <OptionThumbnail
                key={productId}
                thumbnailId={`${optionFragment.id}-${productId}`}
                optionName={displayName}
                imageUrl={featuredImage?.asset?.thumbnail155 ?? undefined}
                debugText={productId}
                size={72}
                onClick={onSelect}
              />
            );
          })}
        />
      )}
      {!asChild && (
        <MissingInsideMapsRenderingWarning
          isSelected={isSelected}
          missingInsideMapsRendering={optionFragment.missingInsideMapsRendering}
        />
      )}
    </div>
  );
}
